// ESM port of cfai-backend/formLogic/evaluateLogic.js — keep these in sync.
// Used client-side for conversational-mode traversal/scoring (fast UX); the server
// re-validates everything on submit using its own copy of this engine.

import { OPERATORS } from "./operators"
import { evaluateExpression } from "./expressionEvaluator"

// NOTE: throughout this module, `field_id` (in conditions) and `target_id` (in jump rules)
// refer to a field's `name` (e.g. "field_3") — the same key used as the answers/responseData
// map key. This avoids an extra fieldId->name indirection when looking up answers.

export function evaluateCondition(condition, answers) {
    const operator = OPERATORS[condition.operator]
    if (!operator) throw new Error(`Unknown operator: ${condition.operator}`)
    const answer = answers[condition.field_id]
    return operator(answer, condition.value)
}

export function evaluateConditionGroup(group, answers) {
    const conditions = group.when || []
    if (conditions.length === 0) return true
    const results = conditions.map((c) => evaluateCondition(c, answers))
    return group.operator_join === "OR" ? results.some(Boolean) : results.every(Boolean)
}

// Evaluates a single question's logic rules against the current answers.
// Returns { visible, next_question_id }.
//
// Visibility semantics (Typeform-style):
// - If the question has any "show" rules, it is hidden by default and becomes
//   visible only if at least one "show" rule matches.
// - "hide" rules, if matched, always hide the question (override "show").
// - The first matching "jump_to" rule determines next_question_id; if none match,
//   next_question_id is null (meaning: proceed to the next field in form order).
export function evaluateLogic(question, answers = {}) {
    // Calculated/score-only fields are never shown to the respondent, regardless of logicRules.
    if (question.isHidden) return { visible: false, next_question_id: null }

    const rules = question.logicRules || []
    const showRules = rules.filter((r) => r.action === "show")
    const hideRules = rules.filter((r) => r.action === "hide")
    const jumpRules = rules.filter((r) => r.action === "jump_to")

    let visible = true
    if (showRules.length > 0) {
        visible = showRules.some((r) => evaluateConditionGroup(r, answers))
    }
    if (visible && hideRules.some((r) => evaluateConditionGroup(r, answers))) {
        visible = false
    }

    let next_question_id = null
    for (const rule of jumpRules) {
        if (evaluateConditionGroup(rule, answers)) {
            next_question_id = rule.target_id
            break
        }
    }

    return { visible, next_question_id }
}

// Given the field the respondent just answered (or null for "start of form"),
// returns the `name` of the next field to show, honoring jump rules and
// skipping fields whose visibility rules evaluate to false. Returns null
// when the form is complete.
export function getNextFieldId(fields, currentFieldName, answers = {}) {
    const nameToIndex = new Map(fields.map((f, i) => [f.name, i]))
    const currentIndex = currentFieldName !== null && currentFieldName !== undefined
        ? nameToIndex.get(currentFieldName)
        : -1

    let nextIndex = (currentIndex ?? -1) + 1

    if (currentIndex !== undefined && currentIndex >= 0) {
        const { next_question_id } = evaluateLogic(fields[currentIndex], answers)
        if (next_question_id && nameToIndex.has(next_question_id)) {
            nextIndex = nameToIndex.get(next_question_id)
        }
    }

    while (nextIndex >= 0 && nextIndex < fields.length) {
        if (evaluateLogic(fields[nextIndex], answers).visible) return fields[nextIndex].name
        nextIndex++
    }
    return null
}

// Simulates a full traversal of the form given a final/complete answers map,
// returning the ordered list of fields that were actually shown (i.e. the
// "asked question set"). Used for Response Intelligence and for validating
// that logic doesn't strand the respondent in a loop.
export function getFormPath(fields, answers = {}, options = {}) {
    const maxSteps = options.maxSteps || fields.length * 2 + 10
    const nameToIndex = new Map(fields.map((f, i) => [f.name, i]))

    const visited = []
    const visitedIndexes = new Set()
    let index = 0
    let steps = 0

    while (index >= 0 && index < fields.length && steps < maxSteps) {
        steps++
        if (visitedIndexes.has(index)) break // jump cycle guard

        const field = fields[index]
        const { visible, next_question_id } = evaluateLogic(field, answers)

        if (!visible) {
            index += 1
            continue
        }

        visited.push(field)
        visitedIndexes.add(index)

        if (next_question_id && nameToIndex.has(next_question_id)) {
            index = nameToIndex.get(next_question_id)
        } else {
            index += 1
        }
    }

    return visited
}

// Converts an answer value to a number for use in scoring/calculations.
// Arrays (multi-select) contribute their length; "yes"/"true" -> 1, "no"/"false" -> 0;
// anything else non-numeric -> null (excluded from the calculation scope).
export function toNumeric(value) {
    if (typeof value === "number") return isNaN(value) ? null : value
    if (Array.isArray(value)) return value.length
    if (typeof value === "string") {
        const trimmed = value.trim().toLowerCase()
        if (trimmed === "") return null
        if (trimmed === "yes" || trimmed === "true") return 1
        if (trimmed === "no" || trimmed === "false") return 0
        const num = Number(trimmed)
        return isNaN(num) ? null : num
    }
    return null
}

// Sums per-option point values for every scoring-enabled field whose options
// were selected, returning the accumulated hidden `score`.
export function computeScore(fields, answers = {}) {
    let score = 0
    for (const field of fields) {
        if (!field.scoring || !field.scoring.enabled) continue
        const optionScores = field.scoring.optionScores
        if (!optionScores) continue

        const answer = answers[field.name]
        if (answer === undefined || answer === null || answer === "") continue

        const selected = Array.isArray(answer) ? answer : [answer]
        for (const sel of selected) {
            const points = optionScores instanceof Map
                ? optionScores.get(String(sel))
                : optionScores[String(sel)]
            if (typeof points === "number") score += points
        }
    }
    return score
}

// Evaluates every `type: "calculated"` field's expression against the current
// answers (numeric-coerced) plus the accumulated `score`. Calculated fields can
// reference other calculated fields defined earlier in the array.
// Returns { score, [fieldName]: number, ... }. Invalid/unevaluable expressions -> null.
export function computeCalculatedFields(fields, answers = {}) {
    const score = computeScore(fields, answers)
    const scope = { score }

    for (const field of fields) {
        if (field.type === "calculated") continue
        const num = toNumeric(answers[field.name])
        if (num !== null) scope[field.name] = num
    }

    const results = {}
    for (const field of fields) {
        if (field.type !== "calculated") continue
        const expression = field.calculation && field.calculation.expression
        if (!expression) {
            results[field.name] = null
            continue
        }
        try {
            const value = evaluateExpression(expression, { ...scope, ...results })
            results[field.name] = value
            scope[field.name] = value
        } catch (e) {
            results[field.name] = null
        }
    }

    results.score = score
    return results
}

// Picks the thank-you screen to show for a completed submission: the first
// screen whose `conditions` match the final answers, falling back to the
// screen marked `isDefault`, falling back to null (caller uses the form's
// generic successMessage).
export function selectThankYouScreen(thankYouScreens, answers = {}) {
    if (!Array.isArray(thankYouScreens) || thankYouScreens.length === 0) return null

    for (const screen of thankYouScreens) {
        if (screen.isDefault) continue
        const conditions = screen.conditions || []
        if (conditions.length > 0 && conditions.every((g) => evaluateConditionGroup(g, answers))) {
            return screen
        }
    }
    return thankYouScreens.find((s) => s.isDefault) || null
}

// Returns a human-readable reason if a set of AND-joined conditions on the SAME field
// can never all be true at once, else null. Deliberately conservative — it only flags
// combinations that are provably impossible, so valid logic is never warned about.
function contradictionReason(conds) {
    const byOp = {}
    for (const c of conds) {
        if (!byOp[c.operator]) byOp[c.operator] = []
        byOp[c.operator].push(c.value)
    }
    const has = (op) => Object.prototype.hasOwnProperty.call(byOp, op)

    if (has("is_empty") && has("is_not_empty")) return "is both empty and not empty"

    if (has("equals")) {
        const vals = [...new Set(byOp.equals.map(String))]
        if (vals.length > 1) return `must equal two different values (${vals.join(", ")})`
        if (has("not_equals") && byOp.not_equals.map(String).includes(vals[0])) {
            return `equals and does not equal "${vals[0]}"`
        }
        if (has("is_empty") && vals[0] !== "") return "equals a value and is empty"
    }

    // Numeric bounds that cannot overlap.
    const nums = (op) => (byOp[op] || []).map(toNumeric).filter((n) => n !== null)
    let maxLower = -Infinity, lowerStrict = false
    for (const v of nums("greater_than")) { if (v >= maxLower) { maxLower = v; lowerStrict = true } }
    for (const v of nums("greater_than_or_equal")) { if (v > maxLower) { maxLower = v; lowerStrict = false } }
    let minUpper = Infinity, upperStrict = false
    for (const v of nums("less_than")) { if (v <= minUpper) { minUpper = v; upperStrict = true } }
    for (const v of nums("less_than_or_equal")) { if (v < minUpper) { minUpper = v; upperStrict = false } }
    if (maxLower !== -Infinity && minUpper !== Infinity) {
        if (minUpper < maxLower || (minUpper === maxLower && (lowerStrict || upperStrict))) {
            return `no number satisfies both bounds (${maxLower} … ${minUpper})`
        }
    }

    if (has("is_one_of") && has("is_not_one_of")) {
        const allowed = new Set([].concat(...byOp.is_one_of).map(String))
        const denied = new Set([].concat(...byOp.is_not_one_of).map(String))
        if (allowed.size > 0 && [...allowed].every((v) => denied.has(v))) {
            return "every allowed option is also excluded"
        }
    }
    return null
}

// Structural validation of a form's logic rules. Returns an array of issue objects
// (empty = no issues found). Catches: references to unknown fields, "show"/"hide"
// rules that reference a later question (can't have been answered yet), jump rules
// targeting unknown/self fields, cycles in the jump graph, questions no path can
// reach, and AND-groups whose conditions contradict each other.
export function validateLogic(fields) {
    const issues = []
    const names = new Set(fields.map((f) => f.name))
    const nameToIndex = new Map(fields.map((f, i) => [f.name, i]))

    fields.forEach((field, idx) => {
        const rules = field.logicRules || []
        rules.forEach((rule, ruleIdx) => {
            (rule.when || []).forEach((cond) => {
                if (!names.has(cond.field_id)) {
                    issues.push({
                        type: "unknown_field_reference",
                        field: field.name,
                        ruleIndex: ruleIdx,
                        reference: cond.field_id
                    })
                    return
                }
                if (rule.action !== "jump_to" && nameToIndex.get(cond.field_id) >= idx) {
                    issues.push({
                        type: "forward_reference",
                        field: field.name,
                        ruleIndex: ruleIdx,
                        reference: cond.field_id
                    })
                }
            })

            if (rule.action === "jump_to") {
                if (!names.has(rule.target_id)) {
                    issues.push({
                        type: "invalid_jump_target",
                        field: field.name,
                        ruleIndex: ruleIdx,
                        target: rule.target_id
                    })
                } else if (rule.target_id === field.name) {
                    issues.push({ type: "self_jump", field: field.name, ruleIndex: ruleIdx })
                }
            }
        })
    })

    // Cycle detection on the jump graph (assumes every jump rule could fire).
    const graph = new Map(fields.map((f) => [
        f.name,
        (f.logicRules || [])
            .filter((r) => r.action === "jump_to" && names.has(r.target_id))
            .map((r) => r.target_id)
    ]))

    const visiting = new Set()
    const visited = new Set()
    function dfs(node, path) {
        if (visiting.has(node)) {
            issues.push({ type: "jump_cycle", cycle: [...path, node] })
            return
        }
        if (visited.has(node)) return
        visiting.add(node)
        for (const next of graph.get(node) || []) dfs(next, [...path, node])
        visiting.delete(node)
        visited.add(node)
    }
    fields.forEach((f) => dfs(f.name, []))

    // Reachability: walk the flow graph from the first question. A question is
    // unreachable when no path can arrive at it — e.g. an earlier unconditional jump
    // always skips past it and nothing jumps back in. Hidden/calculated fields are
    // never rendered, so they're exempt.
    if (fields.length > 0) {
        const successors = (i) => {
            const out = []
            let alwaysJumps = false
            for (const r of fields[i].logicRules || []) {
                if (r.action !== "jump_to" || !names.has(r.target_id)) continue
                out.push(nameToIndex.get(r.target_id))
                if ((r.when || []).length === 0) alwaysJumps = true // fires every time
            }
            if (!alwaysJumps && i + 1 < fields.length) out.push(i + 1)
            return out
        }
        const reachable = new Set()
        const queue = [0]
        while (queue.length) {
            const i = queue.shift()
            if (i === undefined || i < 0 || i >= fields.length || reachable.has(i)) continue
            reachable.add(i)
            for (const n of successors(i)) queue.push(n)
        }
        fields.forEach((f, i) => {
            if (reachable.has(i) || f.isHidden || f.type === "calculated") return
            issues.push({ type: "unreachable_question", field: f.name, label: f.label })
        })
    }

    // Contradictory conditions: an AND-group with mutually exclusive conditions on the
    // same source field can never fire, so the rule is dead.
    fields.forEach((field) => {
        (field.logicRules || []).forEach((rule, ruleIdx) => {
            if ((rule.operator_join || "AND") !== "AND") return
            const byField = new Map()
            for (const c of rule.when || []) {
                if (!byField.has(c.field_id)) byField.set(c.field_id, [])
                byField.get(c.field_id).push(c)
            }
            for (const [fieldId, list] of byField) {
                if (list.length < 2) continue
                const reason = contradictionReason(list)
                if (reason) {
                    issues.push({
                        type: "contradictory_conditions",
                        field: field.name,
                        ruleIndex: ruleIdx,
                        reference: fieldId,
                        reason
                    })
                }
            }
        })
    })

    return issues
}
